import { BaseRecord, HttpError, useWarnAboutChange } from "@refinedev/core";
import {
  UseFormProps,
  UseFormReturnType,
  useForm as useRefineForm,
} from "@refinedev/react-hook-form";
import { useEffect } from "react";
import { FieldValues } from "react-hook-form";

// This hook is a wrapper around the `useRefineForm` hook from the `@refinedev/react-hook-form` package.
// It overrites the existing `warnWhenUnsavedChanges` prop with a new one that is based on the form state.
// Refine only looks if any value changed in the form, this version looks if the form is dirty or not.
export const useForm = <
  TQueryFnData extends BaseRecord = BaseRecord,
  TError extends HttpError = HttpError,
  TVariables extends FieldValues = FieldValues,
  TContext extends object = object,
  TData extends BaseRecord = TQueryFnData,
  TResponse extends BaseRecord = TData,
  TResponseError extends HttpError = TError,
>(
  props?:
    | UseFormProps<
        TQueryFnData,
        TError,
        TVariables,
        TContext,
        TData,
        TResponse,
        TResponseError
      >
    | undefined,
): UseFormReturnType<
  TQueryFnData,
  TError,
  TVariables,
  TContext,
  TData,
  TResponse,
  TResponseError
> => {
  const warnWhenUnsavedChangesProp = props?.warnWhenUnsavedChanges;
  const methods = useRefineForm({ ...props, warnWhenUnsavedChanges: false });
  const {
    formState: { isDirty },
  } = methods;

  const { warnWhenUnsavedChanges: warnWhenUnsavedChangesRefine, setWarnWhen } =
    useWarnAboutChange();

  const warnWhenUnsavedChanges =
    warnWhenUnsavedChangesProp ?? warnWhenUnsavedChangesRefine;

  useEffect(() => {
    setWarnWhen(warnWhenUnsavedChanges && isDirty);
  }, [isDirty, setWarnWhen, warnWhenUnsavedChanges]);

  return methods;
};
